import { useSyncExternalStore } from 'react';
import { NOTES } from '@/content/lab';
import type { Activity } from '@/features/tongue-lab/lessons';

/**
 * Alex's Lab on this device (docs/design/alex-lab.md): whether the reveal was told and answered,
 * which notebook pages carry the child's stamp this round, the round, and how many visits ended
 * (one of Alex's tries each). A game key, so profiles and "Start over" include it.
 */
const KEY = 'foe-names.lab.v1';

export interface LabState {
  /** The reveal: not yet told, told and the child said yes, or told and the child said no. */
  reveal: 'untold' | 'accepted' | 'declined';
  /** Stamped this round, in the order stamped. */
  checked: Activity[];
  /** 1 for the first time through the notebook, 2 for the second look, and so on. */
  round: number;
  /** Visits that ended with Alex's try (picks the next try line). */
  tries: number;
}

const EMPTY: LabState = { reveal: 'untold', checked: [], round: 1, tries: 0 };
const listeners = new Set<() => void>();
let current: LabState = read();

function read(): LabState {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Partial<LabState>) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

function write(next: LabState): void {
  current = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the lab forgets when the page closes.
  }
  for (const l of listeners) l();
}

/** The next page to check: the first unstamped one in notebook order. */
export function nextNote(state: LabState): (typeof NOTES)[number] {
  return NOTES.find((n) => !state.checked.includes(n.activity)) ?? NOTES[0] ?? (NOTES[0] as never);
}

export const labStore = {
  get: (): LabState => current,
  answerReveal: (yes: boolean): void => {
    write({ ...current, reveal: yes ? 'accepted' : 'declined' });
  },
  /** Stamps a page; when every page is stamped, the next round starts with a clean notebook. */
  stamp: (activity: Activity): { roundDone: boolean } => {
    if (current.checked.includes(activity)) return { roundDone: false };
    const checked = [...current.checked, activity];
    if (checked.length >= NOTES.length) {
      write({ ...current, checked: [], round: current.round + 1 });
      return { roundDone: true };
    }
    write({ ...current, checked });
    return { roundDone: false };
  },
  tried: (): void => {
    write({ ...current, tries: current.tries + 1 });
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useLab(): LabState {
  return useSyncExternalStore(labStore.subscribe, labStore.get);
}
